import React from 'react';

const GetProtected = () => (
  <div className="container mx-auto px-4 py-8">
    <h1 className="text-3xl font-bold mb-6">Get Protected</h1>
    <h2 className="text-2xl font-bold mb-4">A Simple Guide to Quantum-Safe Security</h2>
    <hr className="border-gray-300 mb-6" />

    <div className="mb-8">
      <h3 className="text-xl font-bold mb-3">Why You Should Care</h3>
      <p className="mb-4">
        Today's security relies on <strong>cryptography</strong>—the hidden math that keeps websites, messages, and files private.
        Tomorrow's quantum computers will break many of those old math tricks. When that happens, anything still protected by yesterday's methods can be read like an open book.
      </p>
    </div>

    <div className="mb-8">
      <h3 className="text-xl font-bold mb-3">The Hidden Danger: "Store Now, Decrypt Later" (SNDL)</h3>
      <p className="mb-4">
        Attackers are already <strong>saving</strong> encrypted data today, even if they cannot read it yet.
        As soon as quantum computers mature, they will <strong>play back</strong> those recordings and unlock everything inside.
      </p>
      <div className="bg-gray-100 p-4 border-l-4 border-gray-500 italic mb-4">
        Think of someone recording a phone call in a language they don't speak. Years later they learn the language, press <em>play</em>, and understand every word.
      </div>
      <p className="mb-4">
        That is the <strong>Store Now, Decrypt Later</strong> risk—and it means the clock is already ticking.
      </p>
    </div>

    <div className="mb-8">
      <h3 className="text-xl font-bold mb-3">What Has to Happen</h3>
      <ol className="list-decimal pl-6 mb-4">
        <li className="mb-3">
          <strong>Screen Everything</strong>
          <p>Every website, app, server, and cloud service you run must be checked. You need to know which parts still use "classic" cryptography.</p>
        </li>
        <li className="mb-3">
          <strong>Replace the Weak Links</strong>
          <p>Swap unsafe algorithms for <strong>post-quantum cryptography (PQC)</strong>—new math that even quantum computers can't crack.</p>
        </li>
        <li className="mb-3">
          <strong>Stay Up to Date</strong>
          <p>Standards will evolve. Continuous checks make sure you never fall behind.</p>
        </li>
      </ol>
    </div>

    <div className="mb-8">
      <h3 className="text-xl font-bold mb-3">How Our Web Application Helps</h3>
      <ul className="list-disc pl-6 mb-4">
        <li className="mb-2"><strong>Automatic Scan</strong> – Point the tool at your domains; it crawls your public endpoints and lists every cryptographic method in use.</li>
        <li className="mb-2"><strong>Clear Report</strong> – A traffic-light dashboard shows what is safe (green), what needs an update soon (yellow), and what is risky now (red).</li>
        <li className="mb-2"><strong>Action Plan</strong> – Simple, step-by-step advice tells you how to fix each red or yellow item—no PhD required.</li>
      </ul>
    </div>

    <div className="mb-8">
      <h3 className="text-xl font-bold mb-3">The Bottom Line</h3>
      <p className="mb-4">
        Waiting lets attackers capture your data today and unlock it tomorrow. Acting now shields you from the Store Now, Decrypt Later threat and keeps you ahead of quantum-age attacks.
      </p>
    </div>

    <div className="mb-8">
      <h3 className="text-xl font-bold mb-3">Next Steps</h3>
      <ol className="list-decimal pl-6 mb-4">
        <li className="mb-2"><strong>Run the Scan <a href="/" className="text-blue-600 hover:underline">Here</a></strong> – It takes seconds to start.</li>
        <li className="mb-2"><strong>Read the Report</strong> – See your current risks in plain language.</li>
        <li className="mb-2"><strong>Get Protected</strong> – Follow the action plan or ask us to handle the upgrades for you.</li>
      </ol>
    </div>

    <div className="bg-blue-100 p-6 rounded-lg mb-8">
      <p className="text-lg">
        Questions? Ready to begin? <a href="/contact-us" className="text-blue-600 hover:underline">Contact Us Here</a>
      </p>
      <p className="mt-4 font-semibold">
        Stay safe today. Stay safe tomorrow.
      </p>
    </div>
  </div>
);

export default GetProtected;
